import { Star, User, X, ChevronLeft, ChevronRight } from 'lucide-react';
import type React from 'react';
import { useState, useEffect } from 'react';
import { api } from 'src/services/api';

interface ProductReview {
  stars: number;
  title: string;
  image: string | null;
  images?: string[];
  description: string;
  createdAt?: string;
  user: {
    id: string;
    name: string;
    image: string | null;
  };
}

interface ProductReviewsProps {
  productId: string;
}

interface ImageModalProps {
  isOpen: boolean;
  onClose: () => void;
  images: string[];
  currentIndex: number;
  onNavigate: (index: number) => void;
  review: ProductReview;
}

const REVIEWS_PER_PAGE = 5;

const ImageModal: React.FC<ImageModalProps> = ({
  isOpen,
  onClose,
  images,
  currentIndex,
  onNavigate,
  review,
}) => {
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  const minSwipeDistance = 50;

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    const handleKeyNavigation = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        onNavigate(currentIndex === 0 ? images.length - 1 : currentIndex - 1);
      } else if (e.key === 'ArrowRight') {
        onNavigate(currentIndex === images.length - 1 ? 0 : currentIndex + 1);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.addEventListener('keydown', handleKeyNavigation);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.removeEventListener('keydown', handleKeyNavigation);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose, currentIndex, images.length, onNavigate]);

  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      onNavigate(currentIndex === images.length - 1 ? 0 : currentIndex + 1);
    }

    if (isRightSwipe) {
      onNavigate(currentIndex === 0 ? images.length - 1 : currentIndex - 1);
    }
  };

  if (!isOpen) return null;

  const renderStars = (rating: number) => {
    return (
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`w-4 h-4 ${
              star <= rating
                ? 'fill-yellow-400 text-yellow-400'
                : 'text-white/50'
            }`}
          />
        ))}
      </div>
    );
  };

  const getUserImageUrl = (imageName: string | null): string | undefined => {
    if (!imageName) return undefined;
    return `${process.env.REACT_APP_BASE_URL}/uploads/${imageName}`;
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4"
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      <button
        onClick={onClose}
        className="absolute top-4 right-4 z-10 p-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
      >
        <X className="w-6 h-6 text-white" />
      </button>

      {images.length > 1 && (
        <button
          onClick={() =>
            onNavigate(
              currentIndex === 0 ? images.length - 1 : currentIndex - 1
            )
          }
          className="absolute left-4 z-10 p-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
        >
          <ChevronLeft className="w-6 h-6 text-white" />
        </button>
      )}

      <div className="w-full max-w-4xl flex flex-col items-center gap-4">
        <div className="relative w-full">
          <img
            src={images[currentIndex]}
            alt={`Foto ${currentIndex + 1}`}
            className="w-full max-h-[70vh] object-contain rounded-lg select-none"
            onClick={(e) => e.stopPropagation()}
            draggable={false}
          />

          {images.length > 1 && (
            <div className="absolute bottom-4 right-4 bg-black/60 text-white px-3 py-1 rounded-full text-sm">
              {currentIndex + 1} / {images.length}
            </div>
          )}

          {/* {images.length > 1 && (
            <div className="absolute top-1/2 left-0 right-0 -translate-y-1/2 flex justify-between px-4 pointer-events-none md:hidden">
              <div className="text-white/40 text-xs bg-black/40 px-2 py-1 rounded">
                ← Deslize
              </div>
              <div className="text-white/40 text-xs bg-black/40 px-2 py-1 rounded">
                Deslize →
              </div>
            </div>
          )} */}
        </div>

        {/* Informações da Avaliação */}
        <div className="w-full bg-white/10 backdrop-blur-md rounded-lg p-4 text-white">
          <div className="flex items-start gap-3">
            {/* Avatar do usuário */}
            <div className="flex-shrink-0">
              {review.user.image ? (
                <img
                  src={getUserImageUrl(review.user.image)}
                  alt={review.user.name}
                  className="w-10 h-10 rounded-full object-cover border-2 border-white/30"
                />
              ) : (
                <div className="w-10 h-10 bg-red-500 rounded-full flex items-center justify-center">
                  <span className="text-white font-bold text-sm">
                    {review.user.name.charAt(0).toUpperCase()}
                  </span>
                </div>
              )}
            </div>

            {/* Informações */}
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <span className="font-semibold">{review.user.name}</span>
                {renderStars(review.stars)}
              </div>
              <h4 className="font-medium mb-1">{review.title}</h4>
              <p className="text-sm text-white/80 line-clamp-2">
                {review.description}
              </p>
            </div>
          </div>
        </div>

        {/* Miniaturas */}
        {images.length > 1 && (
          <div className="flex gap-2 overflow-x-auto pb-2 max-w-full">
            {images.map((img, idx) => (
              <button
                key={idx}
                onClick={() => onNavigate(idx)}
                className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                  idx === currentIndex
                    ? 'border-red-500 opacity-100'
                    : 'border-white/30 opacity-50 hover:opacity-75'
                }`}
              >
                <img
                  src={img}
                  alt={`Miniatura ${idx + 1}`}
                  className="w-full h-full object-cover"
                />
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Navegação Próxima */}
      {images.length > 1 && (
        <button
          onClick={() =>
            onNavigate(
              currentIndex === images.length - 1 ? 0 : currentIndex + 1
            )
          }
          className="absolute right-4 z-10 p-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
        >
          <ChevronRight className="w-6 h-6 text-white" />
        </button>
      )}
    </div>
  );
};

export const ProductReviews: React.FC<ProductReviewsProps> = ({
  productId,
}) => {
  const [reviews, setReviews] = useState<ProductReview[]>([]);
  const [loadingReviews, setLoadingReviews] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [selectedReview, setSelectedReview] = useState<ProductReview | null>(
    null
  );
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    const fetchReviews = async () => {
      if (!productId) return;

      try {
        setLoadingReviews(true);
        const response = await api.get(`/review/find-all/${productId}`);
        setReviews(response.data || []);
      } catch (error) {
        console.error('Erro ao buscar reviews:', error);
      } finally {
        setLoadingReviews(false);
      }
    };

    fetchReviews();
  }, [productId]);

  const renderStars = (rating: number) => {
    return (
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`w-4 h-4 ${
              star <= rating
                ? 'fill-yellow-400 text-yellow-400'
                : 'text-gray-300'
            }`}
          />
        ))}
      </div>
    );
  };

  const getStarDistribution = () => {
    const distribution = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
    reviews.forEach((review) => {
      if (review.stars >= 1 && review.stars <= 5) {
        distribution[review.stars as keyof typeof distribution]++;
      }
    });
    return distribution;
  };

  const calculateAverageStars = () => {
    if (reviews.length === 0) return 0;
    const totalStars = reviews.reduce((sum, review) => sum + review.stars, 0);
    return totalStars / reviews.length;
  };

  const starDistribution = getStarDistribution();
  const totalReviews = reviews.length;
  const averageStars = calculateAverageStars();

  const totalPages = Math.ceil(totalReviews / REVIEWS_PER_PAGE);
  const startIndex = (currentPage - 1) * REVIEWS_PER_PAGE;
  const endIndex = startIndex + REVIEWS_PER_PAGE;
  const displayedReviews = reviews.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    const reviewsSection = document.getElementById('reviews-section');
    if (reviewsSection) {
      reviewsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '';

    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
      });
    } catch {
      return '';
    }
  };

  const getUserImageUrl = (imageName: string | null): string | undefined => {
    if (!imageName) return undefined;
    return `${process.env.REACT_APP_BASE_URL}/uploads/${imageName}`;
  };

  const getReviewImageUrl = (imageName: string | null): string | undefined => {
    if (!imageName) return undefined;
    return `${process.env.REACT_APP_BASE_URL}/uploads/${imageName}`;
  };

  const handleImageClick = (review: ProductReview, imageIndex: number = 0) => {
    setSelectedReview(review);
    setCurrentImageIndex(imageIndex);
    setImageModalOpen(true);
  };

  const getReviewImages = (review: ProductReview): string[] => {
    const images: string[] = [];

    if (review.images && review.images.length > 0) {
      review.images.forEach((img) => {
        const url = getReviewImageUrl(img);
        if (url) images.push(url);
      });
    } else if (review.image) {
      const url = getReviewImageUrl(review.image);
      if (url) images.push(url);
    }

    return images;
  };

  if (totalReviews === 0 && !loadingReviews) {
    return null;
  }

  return (
    <div id="reviews-section" className="mt-10 border-t pt-8">
      <h2 className="text-2xl font-bold mb-6">Avaliações dos Clientes</h2>

      <div className="bg-gray-50 rounded-lg p-6 mb-6">
        <div className="flex flex-col sm:flex-row gap-6">
          <div className="flex flex-col items-center justify-center sm:border-r sm:pr-6">
            <div className="text-5xl font-bold text-gray-900 mb-2">
              {averageStars.toFixed(1)}
            </div>
            <div className="mb-2">{renderStars(Math.round(averageStars))}</div>
            <div className="text-sm text-gray-600">
              {totalReviews} {totalReviews === 1 ? 'avaliação' : 'avaliações'}
            </div>
          </div>

          <div className="flex-1 space-y-2">
            {[5, 4, 3, 2, 1].map((stars) => {
              const count =
                starDistribution[stars as keyof typeof starDistribution];
              const percentage =
                totalReviews > 0 ? (count / totalReviews) * 100 : 0;

              return (
                <div key={stars} className="flex items-center gap-2">
                  <span className="text-sm text-gray-700 w-20">
                    {stars} {stars === 1 ? 'estrela' : 'estrelas'}
                  </span>
                  <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-yellow-400 transition-all"
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                  <span className="text-sm text-gray-600 w-12 text-right">
                    {count}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="space-y-6">
        {loadingReviews ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-500 mx-auto"></div>
            <p className="text-gray-600 mt-2">Carregando avaliações...</p>
          </div>
        ) : (
          <>
            {displayedReviews.map((review, index) => {
              const reviewImages = getReviewImages(review);

              return (
                <div key={index} className="border-b pb-6 last:border-b-0">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0">
                      {review.user.image ? (
                        <img
                          src={getUserImageUrl(review.user.image)}
                          alt={review.user.name}
                          className="w-12 h-12 rounded-full object-cover border-2 border-gray-200"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                            const fallback =
                              target.nextElementSibling as HTMLElement;
                            if (fallback) fallback.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div
                        className={`w-12 h-12 bg-red-100 rounded-full flex items-center justify-center ${review.user.image ? 'hidden' : ''}`}
                      >
                        <span className="text-red-600 font-bold text-lg">
                          {review.user.name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                    </div>

                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="font-semibold text-gray-900">
                          {review.user.name}
                        </span>
                        {review.createdAt && (
                          <span className="text-sm text-gray-500">
                            • {formatDate(review.createdAt)}
                          </span>
                        )}
                      </div>

                      <div className="mb-2">{renderStars(review.stars)}</div>

                      <h4 className="font-semibold text-gray-900 mb-2">
                        {review.title}
                      </h4>

                      <p className="text-gray-700 mb-3 whitespace-pre-wrap">
                        {review.description}
                      </p>

                      {reviewImages.length > 0 && (
                        <div className="mt-3">
                          {reviewImages.length === 1 ? (
                            <button
                              onClick={() => handleImageClick(review, 0)}
                              className="relative group cursor-pointer overflow-hidden rounded-lg border border-gray-200 hover:border-red-500 transition-all inline-block"
                            >
                              <img
                                src={reviewImages[0]}
                                alt="Foto da avaliação"
                                className="w-20 h-20 sm:w-24 sm:h-24 object-cover group-hover:scale-105 transition-transform"
                                onError={(e) => {
                                  const target = e.target as HTMLImageElement;
                                  target.parentElement?.remove();
                                }}
                              />
                              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                                <div className="text-white opacity-0 group-hover:opacity-100 transition-opacity">
                                  <svg
                                    className="w-6 h-6"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7"
                                    />
                                  </svg>
                                </div>
                              </div>
                            </button>
                          ) : (
                            <div className="relative group">
                              <div
                                className="flex gap-2 overflow-x-auto scroll-smooth scrollbar-hide pb-2"
                                id={`review-carousel-${index}`}
                                style={{ scrollSnapType: 'x mandatory' }}
                              >
                                {reviewImages.map((imgUrl, imgIndex) => (
                                  <button
                                    key={imgIndex}
                                    onClick={() =>
                                      handleImageClick(review, imgIndex)
                                    }
                                    className="relative flex-shrink-0 cursor-pointer overflow-hidden rounded-lg border border-gray-200 hover:border-red-500 transition-all"
                                    style={{ scrollSnapAlign: 'start' }}
                                  >
                                    <img
                                      src={imgUrl}
                                      alt={`Foto ${imgIndex + 1} da avaliação`}
                                      className="w-20 h-20 sm:w-24 sm:h-24 object-cover hover:scale-105 transition-transform"
                                      onError={(e) => {
                                        const target =
                                          e.target as HTMLImageElement;
                                        target.parentElement?.remove();
                                      }}
                                    />
                                    {/* Overlay no hover */}
                                    <div className="absolute inset-0 bg-black/0 hover:bg-black/20 transition-colors flex items-center justify-center">
                                      <div className="text-white opacity-0 hover:opacity-100 transition-opacity">
                                        <svg
                                          className="w-6 h-6"
                                          fill="none"
                                          stroke="currentColor"
                                          viewBox="0 0 24 24"
                                        >
                                          <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7"
                                          />
                                        </svg>
                                      </div>
                                    </div>
                                    {/* Badge de índice */}
                                    <div className="absolute bottom-1 right-1 bg-black/60 text-white text-xs px-1.5 py-0.5 rounded">
                                      {imgIndex + 1}/{reviewImages.length}
                                    </div>
                                  </button>
                                ))}
                              </div>

                              {/* Botão Anterior */}
                              <button
                                onClick={(e) => {
                                  e.preventDefault();
                                  const carousel = document.getElementById(
                                    `review-carousel-${index}`
                                  );
                                  if (carousel) {
                                    carousel.scrollBy({
                                      left: -100,
                                      behavior: 'smooth',
                                    });
                                  }
                                }}
                                className="absolute left-0 top-1/2 -translate-y-1/2 z-10 p-1.5 bg-white/90 hover:bg-white rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity"
                                aria-label="Anterior"
                              >
                                <ChevronLeft className="w-4 h-4 text-gray-700" />
                              </button>

                              {/* Botão Próxima */}
                              <button
                                onClick={(e) => {
                                  e.preventDefault();
                                  const carousel = document.getElementById(
                                    `review-carousel-${index}`
                                  );
                                  if (carousel) {
                                    carousel.scrollBy({
                                      left: 100,
                                      behavior: 'smooth',
                                    });
                                  }
                                }}
                                className="absolute right-0 top-1/2 -translate-y-1/2 z-10 p-1.5 bg-white/90 hover:bg-white rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity"
                                aria-label="Próxima"
                              >
                                <ChevronRight className="w-4 h-4 text-gray-700" />
                              </button>

                              {/* Indicador de scroll (opcional) */}
                              <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 flex gap-1">
                                {reviewImages.map((_, idx) => (
                                  <div
                                    key={idx}
                                    className="w-1.5 h-1.5 rounded-full bg-gray-300"
                                  />
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}

            {totalPages > 1 && (
              <div className="flex justify-center items-center gap-2 pt-6">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    currentPage === 1
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Anterior
                </button>

                <div className="flex gap-1">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                    (page) => {
                      if (
                        page === 1 ||
                        page === totalPages ||
                        (page >= currentPage - 1 && page <= currentPage + 1)
                      ) {
                        return (
                          <button
                            key={page}
                            onClick={() => handlePageChange(page)}
                            className={`w-10 h-10 rounded-lg font-medium transition-colors ${
                              currentPage === page
                                ? 'bg-red-600 text-white'
                                : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                            }`}
                          >
                            {page}
                          </button>
                        );
                      } else if (
                        page === currentPage - 2 ||
                        page === currentPage + 2
                      ) {
                        return (
                          <span
                            key={page}
                            className="w-10 h-10 flex items-center justify-center text-gray-500"
                          >
                            ...
                          </span>
                        );
                      }
                      return null;
                    }
                  )}
                </div>

                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    currentPage === totalPages
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Próxima
                </button>
              </div>
            )}

            {totalPages > 1 && (
              <div className="text-center text-sm text-gray-600 mt-4">
                Mostrando {startIndex + 1} - {Math.min(endIndex, totalReviews)}{' '}
                de {totalReviews} avaliações
              </div>
            )}
          </>
        )}
      </div>

      {selectedReview && (
        <ImageModal
          isOpen={imageModalOpen}
          onClose={() => setImageModalOpen(false)}
          images={getReviewImages(selectedReview)}
          currentIndex={currentImageIndex}
          onNavigate={setCurrentImageIndex}
          review={selectedReview}
        />
      )}
    </div>
  );
};
